import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { genDDMenuItemLabel } from '../../Components/QueryPanel/models';
import Spinner from '../../Components/Spinner/Spinner';
import { selectAccountLoginLoading, selectIsLoggedIn } from '../../redux/account';
import { ExportFormat, isType } from '../../shared';
import { Admin, AdminReport, AdminStudent, AdminStudentsResponse,
  REPORT_TYPES } from '../../shared';
import {fetchAdminStudents} from './models';
import './TestResults.scss';
import useAssessments from '../../hooks/assessments/useAssessments';
import useAdminReports from '../../hooks/adminReports/useAdminReports';
import StudentItemDetail from '../../Components/StudentItemDetail/StudentItemDetail';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router';
import { selectControls } from '../../redux/controls';
import { useLocation } from 'react-router';

import { ReportArgumentLocationState } from '../ResultReport/models';
import StudentItem from '../../Components/StudentItem/StudentItem';
import StudentComparison from '../../Components/StudentComparison/StudentComparison';
import { ExportModal } from '../../Components/Modals/ExportModal/ExportModal';

const staticMenuStyle = {
  width: '100%'
};

export const TestResults = (): JSX.Element => {

  const navigate = useNavigate();
  const location = useLocation();
  const controls = useSelector(selectControls);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isLoggedInLoading = useSelector(selectAccountLoginLoading);

  const {assessments, assessmentsLoading} = useAssessments(isLoggedIn);
  const {reports, reportsLoading} = useAdminReports(isLoggedIn);

  const [selectedAssessment, setSelectedAssessment] = useState<Admin|null>(null);
  const [selectedReport, setSelectedReport] = useState<AdminReport|null>(null);
  const [adminStudents, setAdminStudents] = useState<AdminStudent[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [reportOnScreen, setReportOnScreen] = useState<boolean>(false);

  const [printSignal, setPrintSignal] = useState<boolean>(false);
  const [exportOpen, setExportOpen] = useState<boolean>(false);
  const [exportSignal, setExportSignal] = useState<ExportFormat>('');


  /**
   * onClick Handler for Assessment dropdown menu
   */
  const onClickAssessment = useCallback((admin: Admin) => {
    if (admin && typeof admin !== null) {
      setSelectedAssessment(admin);

      const processFetchAdminStudents = async() => {
        setIsLoading(true);
        setSelectedReport(null);
        const response = await fetchAdminStudents(admin);

        if (isType<AdminStudentsResponse>(response, 'AdminStudentsResponse')) {
          setAdminStudents(response.students);
        } else {
          setAdminStudents([]);
        }
        setIsLoading(false);
      };
      processFetchAdminStudents();
    }
  }, []);

  /**
   * onClick Handler for Assessment dropdown menu
   */
  const onClickReport = useCallback((report: AdminReport) => {
    if (report && typeof report !== null) {
      setSelectedReport(report);
    }
  }, []);

  const printHandler = useCallback(() => {
    setPrintSignal(true);
    setTimeout(() => {
      setPrintSignal(false);
    }, 0);
  }, [setPrintSignal]);


  const openExportDialog = useCallback(() => {
    setExportOpen(true);
  }, [setExportOpen]);

  const exportHandler = useCallback((result, format) => {
    if (!result) {
      setExportOpen(false);
      setExportSignal('');
      return;
    }
    setExportSignal(format);
    setTimeout(() => {
      setExportSignal('');
      setExportOpen(false);
    }, 0);
  }, [setExportSignal]);

  const returnReportComponent = useCallback((
    admin: Admin|null,
    report: AdminReport|null,
    students: AdminStudent[],
    setReportOnScreen: React.Dispatch<React.SetStateAction<boolean>>
  ): JSX.Element => {
    if (!admin) return <></>;

    switch(report?.rptType) {
    case REPORT_TYPES.STUDENTITEMDETAIL:
      return (<StudentItemDetail admin={admin} students={students} setReportOnScreen={setReportOnScreen} printSignal={printSignal} exportSignal={exportSignal} fromQuiz={false} />);
    case REPORT_TYPES.STUDENTITEM:
      return (<StudentItem admin={admin} students={students} showReportSelectors={true} setReportOnScreen={setReportOnScreen} fromQuiz={false}
        printSignal={printSignal} exportSignal={exportSignal} origUrl='' />);
    case REPORT_TYPES.COMPARISON:
      return (<StudentComparison admin={admin} setReportOnScreen={setReportOnScreen} printSignal={printSignal} exportSignal={exportSignal} />);
    default:
      return (<></>);
    }
  }, [exportSignal, printSignal]);

  useLayoutEffect(() => {
    if (!isLoggedInLoading && !isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, isLoggedInLoading, navigate]);


  /**
   * Set loading spinner
   */
  useEffect(() => {
    if (assessmentsLoading || reportsLoading) {
      if (assessments.length > 0){
        if (location.state) {
          setIsLoading(true);
          const AssessmentLocationState = location.state as ReportArgumentLocationState;
          const isAdminID = AssessmentLocationState.administrationID;
          assessments.map((asObj) => {
            if (asObj.administrationID === isAdminID) {
              setSelectedAssessment(asObj);
              const processFetchAdminStudents = async() => {
                setIsLoading(true);
                setSelectedReport(null);
                const response = await fetchAdminStudents(asObj);
                if (isType<AdminStudentsResponse>(response, 'AdminStudentsResponse')) {
                  setAdminStudents(response.students);
                } else {
                  setAdminStudents([]);
                }
                setIsLoading(false);
              };
              processFetchAdminStudents();
            }
          });

          if (reports.length > 0){
            reports.length > 0 &&
            reports.map((rptObj) => {
              if ( rptObj && typeof rptObj !== null) {
                setSelectedReport(rptObj);
              }
            });
          }
        } else {
          // console.log('TCL: location.state does not exist 2', location.state);
          setIsLoading(false);
        }
      }
      setIsLoading(true);
    } else {
      if (location.state) {
        // console.log('TCL: location.state 3', location.state);
      } else {
        setIsLoading(false);
      }
    }
  }, [assessmentsLoading, reportsLoading, assessments, reports, location.state]);


  // clears background scrolling on init
  useEffect(() => {
    if(document.body.classList.contains('bg-noscroll')){
      document.body.classList.remove('bg-noscroll');
    }
  },[]);

  return (
    <main className='nqt-test-results'>
      <ExportModal open={exportOpen} onClose={(result, format) => exportHandler(result, format)} disableExcel={selectedReport?.rptType === REPORT_TYPES.STUDENTITEMDETAIL}  />
      <section className="nqt-tr--header d-flex justify-content-end pb-3">
        <div className="nqt-tr--print-export-container">
          <button className="nqt-print-btn nqt-clear-btn"
            onClick={printHandler}
            disabled={!reportOnScreen}>
            <span className="nqt-print-label">{controls?.Rpt_Print?.controlText}</span>
            <FontAwesomeIcon className='nqt-print-icon fa-signout-alt' icon={faPrint} />
          </button>
          <button className="nqt-export-btn nqt-clear-btn"
            disabled={!reportOnScreen}
            onClick={openExportDialog}>
            <span className="nqt-export-label">{controls?.Rpt_Export?.controlText}</span>
            <FontAwesomeIcon className='nqt-export-icon fa-signout-alt' icon={faSignOutAlt} />
          </button>
        </div>
      </section>
      <section className="nqt-tr--selectors-container">
        {
          isLoading && <Spinner size={80}/>
        }
        <div className="nqt-tr--selector-container">
          <h3 className="nqt-tr--selector-header">
            {
              controls?.Rpt_SelectAssessment?.controlText
            }
          </h3>
          <div className="dropdown">
            <button className="nqt-dropdown-btn btn dropdown-toggle"
              disabled={assessments.length === 0}
              type="button"
              id="dropdownAssessments"
              data-bs-toggle="dropdown"
              aria-expanded="false">
              <span className='dropdown-interior'>
                {
                  selectedAssessment ?
                    selectedAssessment.administrationName :
                    controls?.Rpt_SelectAssessmentDflt?.controlText
                }

              </span>
              <span className='dropdown-arrow'></span>
            </button>
            <ul className="dropdown-menu" style={staticMenuStyle} aria-labelledby="dropdownAssessments">
              {genDDMenuItemLabel(controls?.Rpt_SelectAssessmentDflt?.controlText)}
              {
                assessments.length > 0 &&
                assessments.map((asObj, ind) => (
                  <li key={`tr-assess-${ind}`}>
                    <a className={'dropdown-item' + (asObj.administrationID === selectedAssessment?.administrationID ? ' selected' : '')}
                      onClick={(e) => {e.preventDefault(); onClickAssessment(asObj);}}
                      href="#">
                      {asObj.administrationName}
                    </a>
                  </li>
                ))
              }
            </ul>
          </div>
        </div>
        <div className="nqt-tr--selector-container">
          <h3 className="nqt-tr--selector-header">
            {
              controls?.Rpt_SelectReport?.controlText
            }
          </h3>
          <div className="dropdown">
            <button className="nqt-dropdown-btn btn dropdown-toggle"
              disabled={reports.length === 0 || adminStudents.length === 0}
              type="button"
              id="dropdownReports"
              data-bs-toggle="dropdown"
              aria-expanded="false">
              <span className='dropdown-interior'>
                {
                  selectedReport ?
                    selectedReport.reportLabel :
                    controls?.Rpt_SelectReportDflt?.controlText
                }
              </span>
              <span className='dropdown-arrow'></span>
            </button>
            <ul className="dropdown-menu" style={staticMenuStyle} aria-labelledby="dropdownReports">
              {genDDMenuItemLabel('Select Report')}
              {
                reports.length > 0 &&
                reports.map((rptObj, ind) => (
                  <li key={`tr-rpt-${ind}`}>
                    <a className={'dropdown-item' + (rptObj.reportLabel === selectedReport?.reportLabel ? ' selected' : '')}
                      onClick={(e) => {e.preventDefault(); onClickReport(rptObj);}} href="#">
                      {rptObj.reportLabel}
                    </a>
                  </li>
                ))
              }
            </ul>
          </div>
        </div>
      </section>
      <div className="nqt-tr--results" id="report-print-anchor">
        {
          returnReportComponent(selectedAssessment, selectedReport, adminStudents, setReportOnScreen)
        }
      </div>
    </main>
  );
};

export default TestResults;